import React, { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { UploadSimple, Image as ImageIcon, X, CircleNotch, Crop } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageCropperModal from "./ImageCropperModal";

/**
 * Image upload with an interactive crop / zoom / rotate editor.
 * Accepts an initial `value` (URL) and calls `onChange(url)` after successful upload.
 *
 * When a user picks a file we open the cropper modal so they can adjust the image
 * (and preview exactly how it will render on the site) before we upload. This
 * prevents the "image is being cropped weirdly" problem where the display container's
 * aspect ratio doesn't match the source image.
 *
 * Props:
 *   value       existing image URL
 *   onChange    (url) => void
 *   label       label shown above
 *   aspect      Tailwind class used for the preview slot (e.g. "aspect-square")
 *   cropAspect  numeric aspect ratio for the cropper (1 = square, 3 = wide banner).
 *               Defaults to a value derived from `aspect`.
 *   testId      test id prefix
 *   maxMb       upload size cap (default 8MB before cropping; cropper output is
 *               downscaled server-side friendly to ~2000px max dimension)
 */
export default function ImageUpload({
  value,
  onChange,
  label,
  aspect = "aspect-square",
  cropAspect,
  testId,
  maxMb = 8,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [cropperSrc, setCropperSrc] = useState(null);

  const previewUrl = value
    ? (value.startsWith("http") ? value : `${API.replace(/\/api$/, "")}${value}`)
    : null;

  // Derive a numeric aspect from the CSS class if not explicitly passed.
  const resolvedAspect = cropAspect || aspectFromClass(aspect);

  const openCropperFor = (file) => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Image too large (max ${maxMb}MB)`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropperSrc(reader.result);
    reader.onerror = () => toast.error("Could not read image");
    reader.readAsDataURL(file);
  };

  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      // Use a .jpg extension since we always encode as JPEG in the cropper
      const filename = `crop_${Date.now()}.jpg`;
      fd.append("file", blob, filename);
      const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange && onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange && onChange("");
  };

  return (
    <div>
      {label && <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">{label}</div>}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); openCropperFor(e.dataTransfer.files?.[0]); }}
        className={`relative ${aspect} w-full max-w-full rounded-lg border-2 border-dashed cursor-pointer overflow-hidden group transition-colors ${
          dragging ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/20" : previewUrl ? "border-border" : "border-border hover:border-emerald-500"
        }`}
        data-testid={testId || "image-upload"}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => { openCropperFor(e.target.files?.[0]); e.target.value = ""; }}
          data-testid={testId ? `${testId}-input` : "image-upload-input"}
        />
        {previewUrl ? (
          <>
            <img src={previewUrl} alt="preview" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="text-white text-xs font-medium flex items-center gap-1"><Crop size={14} /> Replace / adjust</div>
            </div>
            <button
              type="button"
              onClick={clear}
              className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
              title="Remove"
              data-testid={testId ? `${testId}-clear` : "image-upload-clear"}
            >
              <X size={12} />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-muted-foreground">
            <div className="text-center px-3">
              {uploading ? (
                <CircleNotch size={22} className="animate-spin mx-auto" />
              ) : (
                <>
                  <ImageIcon size={22} className="mx-auto mb-1.5" />
                  <div className="text-[11px] font-medium">Drop or click to upload</div>
                  <div className="text-[10px] mt-0.5 text-muted-foreground/70">Then crop & adjust · Max {maxMb}MB</div>
                </>
              )}
            </div>
          </div>
        )}
        {uploading && previewUrl && (
          <div className="absolute inset-0 bg-black/50 grid place-items-center">
            <CircleNotch size={22} className="animate-spin text-white" />
          </div>
        )}
      </div>

      <ImageCropperModal
        open={!!cropperSrc}
        src={cropperSrc}
        aspect={resolvedAspect}
        label={label ? `Adjust ${label.toLowerCase()}` : "Adjust image"}
        onCancel={() => setCropperSrc(null)}
        onConfirm={async (blob) => {
          setCropperSrc(null);
          await uploadBlob(blob);
        }}
      />
    </div>
  );
}

function aspectFromClass(cls = "") {
  // Parse Tailwind aspect-* values into a numeric ratio (w / h).
  if (cls.includes("aspect-square")) return 1;
  const bracket = cls.match(/aspect-\[(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)\]/);
  if (bracket) return parseFloat(bracket[1]) / parseFloat(bracket[2]);
  const video = cls.includes("aspect-video");
  if (video) return 16 / 9;
  return 1;
}
